import { useState, useEffect } from 'react';
import { Sparkles, Mic, Send, Minimize2, Maximize2, X, Lightbulb } from 'lucide-react';

interface Message {
  id: string;
  type: 'user' | 'ai';
  content: string;
  timestamp: Date;
}

export default function FloatingAIAssistant() {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMinimized, setIsMinimized] = useState(true);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      type: 'ai',
      content: "Hi! I'm your AI Care Copilot. I can help you analyze member data, generate care plans, predict risks, and optimize workflows. What would you like to do?",
      timestamp: new Date()
    }
  ]);
  const [input, setInput] = useState('');
  const [isThinking, setIsThinking] = useState(false);
  const [contextSuggestion, setContextSuggestion] = useState('');

  const suggestions = [
    "Analyze high-risk members",
    "Generate care plan for member",
    "Find quality measure gaps",
    "Predict readmission risk",
    "Optimize outreach timing"
  ];

  useEffect(() => {
    const contextSuggestions = [
      "I notice you're viewing the dashboard. Would you like me to analyze the risk trends?",
      "Tip: I can automatically identify members who need immediate attention",
      "I can help you prioritize your care team's daily tasks based on AI insights",
      "Would you like me to predict which gaps are most likely to close this quarter?"
    ];

    const timer = setInterval(() => {
      if (!isExpanded && Math.random() > 0.7) {
        setContextSuggestion(contextSuggestions[Math.floor(Math.random() * contextSuggestions.length)]);
        setTimeout(() => setContextSuggestion(''), 5000);
      }
    }, 15000);

    return () => clearInterval(timer);
  }, [isExpanded]);

  const handleSend = () => {
    if (!input.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: input,
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage]);
    setInput('');
    setIsThinking(true);

    setTimeout(() => {
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        type: 'ai',
        content: `I understand you want to "${input}". Let me analyze the data and prepare insights for you. This would integrate with your actual AI backend to provide real-time analysis.`,
        timestamp: new Date()
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsThinking(false);
    }, 2000);
  };

  if (isMinimized) {
    return (
      <button
        onClick={() => setIsMinimized(false)}
        className="fixed bottom-6 right-6 w-16 h-16 bg-gradient-to-r from-purple-900 to-purple-700 rounded-full shadow-2xl flex items-center justify-center hover:scale-110 transition-transform z-50 group"
      >
        <Sparkles className="w-7 h-7 text-white animate-pulse" />
        {contextSuggestion && (
          <div className="absolute bottom-full right-0 mb-3 w-64 p-3 bg-white rounded-lg shadow-xl border border-blue-200 animate-in fade-in slide-in-from-bottom-2">
            <div className="flex items-start gap-2">
              <Lightbulb className="w-4 h-4 text-yellow-500 flex-shrink-0 mt-0.5" />
              <p className="text-sm text-gray-700">{contextSuggestion}</p>
            </div>
          </div>
        )}
        <div className="absolute -top-1 -right-1 w-4 h-4 bg-green-500 rounded-full border-2 border-white animate-pulse" />
      </button>
    );
  }

  return (
    <div className={`fixed bottom-6 right-6 bg-white rounded-2xl shadow-2xl border border-gray-200 z-50 transition-all duration-300 ${isExpanded ? 'w-96 h-[600px]' : 'w-96 h-[500px]'
      }`}>
      <div className="flex flex-col h-full">
        <div className="flex items-center justify-between px-4 py-3 bg-gradient-to-r from-purple-900 to-purple-700  rounded-t-2xl">
          <div className="flex items-center gap-2">
            <div className="relative">
              <Sparkles className="w-5 h-5 text-white animate-pulse" />
              <div className="absolute -top-1 -right-1 w-2 h-2 bg-green-400 rounded-full animate-pulse" />
            </div>
            <div>
              <h3 className="font-semibold text-white">AI Copilot</h3>
              <p className="text-xs text-blue-100">Always learning, always helping</p>
            </div>
          </div>
          <div className="flex items-center gap-1">
            <button
              onClick={() => setIsExpanded(!isExpanded)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              {isExpanded ? (
                <Minimize2 className="w-4 h-4 text-white" />
              ) : (
                <Maximize2 className="w-4 h-4 text-white" />
              )}
            </button>
            <button
              onClick={() => setIsMinimized(true)}
              className="p-1.5 hover:bg-white/20 rounded-lg transition-colors"
            >
              <X className="w-4 h-4 text-white" />
            </button>
          </div>
        </div>

        <div className="flex-1 overflow-y-auto p-4 space-y-4">
          {messages.map((message) => (
            <div
              key={message.id}
              className={`flex ${message.type === 'user' ? 'justify-end' : 'justify-start'}`}
            >
              <div
                className={`max-w-[80%] rounded-2xl px-4 py-2.5 ${message.type === 'user'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white'
                  : 'bg-gray-100 text-gray-900'
                  }`}
              >
                {message.type === 'ai' && (
                  <div className="flex items-center gap-2 mb-1">
                    <Sparkles className="w-3 h-3 text-blue-600" />
                    <span className="text-xs font-medium text-blue-600">AI Copilot</span>
                  </div>
                )}
                <p className="text-sm leading-relaxed">{message.content}</p>
              </div>
            </div>
          ))}

          {isThinking && (
            <div className="flex justify-start">
              <div className="bg-gray-100 rounded-2xl px-4 py-3">
                <div className="flex items-center gap-2">
                  <div className="flex gap-1">
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                    <div className="w-2 h-2 bg-blue-600 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                  <span className="text-sm text-gray-600">AI is thinking...</span>
                </div>
              </div>
            </div>
          )}
        </div>

        <div className="px-4 py-3 border-t border-gray-200">
          <div className="flex flex-wrap gap-2 mb-3">
            {suggestions.slice(0, 3).map((suggestion, index) => (
              <button
                key={index}
                onClick={() => setInput(suggestion)}
                className="px-3 py-1.5 bg-gray-100 hover:bg-gray-200 rounded-full text-xs text-gray-700 transition-colors"
              >
                {suggestion}
              </button>
            ))}
          </div>

          <div className="flex items-center gap-2">
            <button className="p-2.5 bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors">
              <Mic className="w-5 h-5 text-gray-600" />
            </button>
            <input
              type="text"
              value={input}
              onChange={(e) => setInput(e.target.value)}
              onKeyPress={(e) => e.key === 'Enter' && handleSend()}
              placeholder="Ask AI anything..."
              className="flex-1 px-4 py-2.5 bg-gray-100 rounded-xl border-none outline-none focus:ring-2 focus:ring-blue-500 transition-shadow"
            />
            <button
              onClick={handleSend}
              disabled={!input.trim()}
              className="p-2.5 bg-gradient-to-r from-blue-600 to-purple-600 hover:from-blue-700 hover:to-purple-700 disabled:opacity-50 disabled:cursor-not-allowed rounded-xl transition-all"
            >
              <Send className="w-5 h-5 text-white" />
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
